/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unused-prop-types */
import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import Input from 'src/components/ui/Input/Input';
import Checkbox from 'src/components/ui/Checkbox/Checkbox';
import Divider from 'src/components/ui/Divider/Divider';
import Select, { Option } from 'src/components/ui/Select/Select';
import { SingleValue } from 'react-select';
import TablePagination from 'src/components/ui/TablePagination/TablePagination';
import { getPaginationOptions } from 'src/utils/general';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useSaleOrderUpdate } from 'src/services/saleOrder';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import useStockInventory, { StockItem } from 'src/services/inventory';

import { Product, useAllProducts } from 'src/services/productsNew';
import styles from './SelectProducts.module.scss';

export enum PortalProductType {
  Set = 'set',
  Stockable = 'stockable',
  Digital = 'digital',
}

export interface SelectProductsProps {
  saleOrderId?: number;
  defaultValue?: SelectedProductsProduct[];
  displayOptions?: { headline?: boolean };
  onUpdateSuccess?: (products: SelectedProductsProduct[]) => void;
  selectProductsData: UseSelectProductsDataResult;
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
  onPaginationClick: (direction: 'previous' | 'next') => void;
  onSearchQueryChange: (searchQuery: string) => void;
  onPageSizeChange: (pageSize: number) => void;
  showBadges?: boolean;
}

export interface UseSelectProductsDataResult {
  items: StockItem[] | Product[];
  pages: number;
}

export interface SelectedProductsProduct {
  id?: string | number;
  name: string;
  product_id: number;
  barcode?: string;
  article_number?: string;
  quantity?: number;
  tracking?: string | null;
}

const getAvailabilityBadgeType = (value: number): BadgeType => {
  if (value > 0) return BadgeType.Successful;
  if (value < 0) return BadgeType.Error;
  return BadgeType.Neutral;
};

const SelectProducts: React.FC<SelectProductsProps> = ({
  saleOrderId = undefined,
  defaultValue = [],
  displayOptions,
  onUpdateSuccess,
  selectProductsData,
  pageIndex,
  pageSize,
  searchQuery,
  onPaginationClick,
  onSearchQueryChange,
  onPageSizeChange,
  showBadges,
}) => {
  const { t } = useTranslation();
  const { items, pages } = selectProductsData;
  const updateMutation = useSaleOrderUpdate();

  const [selectedProducts, setSelectedProducts] = useState<SelectedProductsProduct[]>(defaultValue);
  const options = getPaginationOptions();

  const getProductId = (product: StockItem | Product) => {
    if ('product_id' in product) {
      return product.product_id;
    }
    return product.id;
  };

  const addOrRemoveProduct = (id: number) => {
    const index = selectedProducts.findIndex(e => e.product_id === id || e.id === id);
    if (index === -1) {
      const product = items.find(e => getProductId(e) === id || e.id === id);
      if (product) {
        const res: SelectedProductsProduct = {
          ...product,
          product_id: (getProductId(product) as number) ?? (product.id as number),
          id: getProductId(product) as number,
          barcode: product.barcode as string,
          article_number: product.article_number as string,
        };
        setSelectedProducts([...selectedProducts, res]);
      }
    } else {
      setSelectedProducts(selectedProducts.filter(e => e.product_id !== id));
    }
  };

  const selectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // add all items to selectedProducts if not already added
      const newSelectedProducts = items
        .filter(product => selectedProducts.findIndex(e => e.product_id === getProductId(product)) === -1)
        .map(p => ({
          ...p,
          product_id: (getProductId(p) as number) ?? (p.id as number),
          id: p.id as unknown as string,
          barcode: p.barcode as string,
          article_number: p.article_number as string,
        }));
      setSelectedProducts([...selectedProducts, ...newSelectedProducts]);
    } else {
      // remove all items from selectedProducts that are in items
      setSelectedProducts(
        selectedProducts.filter(e => items.findIndex(product => getProductId(product) === e.product_id) === -1),
      );
    }
  };

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      const pageSize = parseInt(selectedOption.value as string, 10);
      onPageSizeChange(pageSize);
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    onPaginationClick(direction);
  };

  const handleAddProducts = async () => {
    if (!saleOrderId) {
      if (onUpdateSuccess) onUpdateSuccess(selectedProducts);
      return;
    }
    const data = {
      order_lines: selectedProducts.map(product => ({
        product_id: product.product_id,
        quantity: 1,
      })),
    };
    try {
      await updateMutation.mutateAsync({
        params: {
          path: {
            sale_order_id: saleOrderId,
          },
        },
        body: data as any,
      });
      if (onUpdateSuccess) onUpdateSuccess(selectedProducts);
      toast.success(t('success.add_product_to_sale_order_success'));
    } catch (error: any) {
      if (typeof error?.detail === 'string' && error.detail.includes('Lot is required for product with tracking')) {
        toast.error(t('errors.lot_required_for_tracked_product'), { autoClose: 5000 });
      } else {
        toast.error(t('errors.add_product_to_sale_order_failed'));
      }
    }
  };

  const allSelected =
    items.length > 0 &&
    items.every(product => selectedProducts.findIndex(e => e.product_id === getProductId(product)) !== -1);

  return (
    <div className={classNames(styles.root)}>
      {!displayOptions?.headline && <h3 className="typo-title-24 text-full mb-20">Select Products</h3>}
      <Input
        value={searchQuery}
        clearable
        className="mb-20"
        onChange={e => onSearchQueryChange(e.target.value)}
        placeholder={t('general.search_by_barcode_or_name')}
      />
      <div className="flex items-center gap-8 mb-20 w-full">
        <div className="flex gap-8">
          <Checkbox onChange={selectAll} checked={allSelected} />
          <span className="typo-text-14 text-full">{t('general.choose_all')}</span>
        </div>
        {showBadges && (
          <div className="flex items-center gap-8 ml-auto flex-1 justify-end flex-shrink-0 flex-wrap">
            <Badge type={BadgeType.Neutral} expanded label={t('legend.not_reserved')} />
          </div>
        )}
      </div>
      <div className={classNames(styles.productsContainer, 'grid grid-cols-2')}>
        {!items ||
          (items.length === 0 && <div className="typo-text-12 text-faded">{t('general.no_products_found')}</div>)}
        {items.map(product => {
          const isAdded =
            selectedProducts.findIndex(e => e.product_id === getProductId(product) || e.id === product.id) !== -1;
          return (
            <div
              key={`${product.id}-${getProductId(product)}`}
              role="none"
              onClick={() => {
                const productId = getProductId(product);
                if (productId !== undefined && productId !== null) {
                  addOrRemoveProduct(productId);
                }
              }}
              className={classNames(styles.product, { [styles.isAdded]: isAdded }, 'col-span-2 md:col-span-1')}>
              <Checkbox disabled checked={isAdded} onChange={() => {}} />
              <div className="flex flex-col gap-4">
                <span className="typo-text-12--bold flex items-center gap-4">
                  {product.name}

                  {'not_reserved' in product && (
                    <Badge
                      hoverText={t('outbound.not_reserved')}
                      type={getAvailabilityBadgeType(product.not_reserved)}
                      expanded
                      label={product.not_reserved.toString()}
                      className="ml-2"
                    />
                  )}
                </span>
                <span className="typo-text-12 text-faded">
                  {product.article_number as any} | {product.barcode as any}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <Divider className="mt-20 mb-20" />
      <div className="w-full  flex items-center">
        <div className="flex gap-x-10 flex-1 items-center">
          <div>
            <Select
              onChange={handlePerPageUpdate}
              options={options}
              defaultValue={options.find(option => option.value === pageSize.toString())}
            />
          </div>
          <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
        </div>
        <div className="ml-auto">
          <TablePagination maxPage={pages} onPaginationClick={handleTablePaginationClick} currentPage={pageIndex} />
        </div>
      </div>
      <Button
        onClick={handleAddProducts}
        className="ml-auto mt-40"
        tone={ButtonToneProps.PRIMARY_BLUE}
        title={t('general.add_products')}
      />
    </div>
  );
};

export default SelectProducts;

interface SelectProductsFromInventoryProps {
  saleOrderId?: number;
  defaultValue?: SelectedProductsProduct[];
  displayOptions?: { headline?: boolean };
  onUpdateSuccess?: (products: SelectedProductsProduct[]) => void;
  showBadges?: boolean;
  hideProduct?: number;
  excludeBomComponents?: boolean;
}

export const SelectProductsFromInventory: React.FC<SelectProductsFromInventoryProps> = ({
  saleOrderId = undefined,
  defaultValue = [],
  displayOptions,
  onUpdateSuccess,
  showBadges,
  hideProduct,
  excludeBomComponents = false,
}) => {
  const {
    stockInventory: { items, pages },
    getStockInventory,
    // Only confirmed ("Aktiv") products may be added to inbounds/outbounds;
    // draft products are hidden from the picker. AND-ed with any search.
  } = useStockInventory(undefined, {
    baseAndDomain: [{ key: 'status', value: 'confirmed', operator: '=' }],
  });

  const data: UseSelectProductsDataResult = useMemo(
    () => ({
      items: hideProduct
        ? items.filter(item => item.product_id !== hideProduct)
        : excludeBomComponents
          ? items.filter(item => item.portal_product_type !== PortalProductType.Set)
          : items,
      pages: pages as number,
      getStockInventory,
    }),
    [items, pages, hideProduct],
  );

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <SelectProducts
      searchQuery={searchQuery}
      onSearchQueryChange={searchQuery => {
        getStockInventory(
          1,
          pageSize,
          searchQuery
            ? [
                { key: 'name', value: searchQuery, operator: 'ilike' },
                { key: 'barcode', value: searchQuery, operator: 'ilike' },
                { key: 'default_code', value: searchQuery, operator: 'ilike' },
              ]
            : undefined,
        );
        setSearchQuery(searchQuery);
      }}
      pageSize={pageSize}
      onPageSizeChange={pageSize => {
        getStockInventory(
          pageIndex + 1,
          pageSize,
          excludeBomComponents ? [{ key: 'portal_product_type', value: PortalProductType.Set, operator: '!=' }] : [],
        );
        setPageSize(pageSize);
      }}
      pageIndex={pageIndex}
      onPaginationClick={direction => {
        const newPageIndex = direction === 'previous' ? pageIndex - 1 : pageIndex + 1;
        getStockInventory(
          newPageIndex + 1,
          pageSize,
          excludeBomComponents ? [{ key: 'portal_product_type', value: PortalProductType.Set, operator: '!=' }] : [],
        );
        setPageIndex(newPageIndex);
      }}
      {...{ saleOrderId, defaultValue, displayOptions, onUpdateSuccess }}
      selectProductsData={data}
      showBadges={showBadges}
    />
  );
};

export const SelectProductsFromAllProducts: React.FC<SelectProductsFromInventoryProps> = ({
  saleOrderId = undefined,
  defaultValue = [],
  displayOptions,
  onUpdateSuccess,
  showBadges,
}) => {
  // The filter arg was provided by the backend team. The ("status", "=",
  // "confirmed") term restricts the picker to confirmed ("Aktiv") products;
  // draft products are hidden. (Implicit AND with the type/variant terms.)
  const domainFilter = `["&", ("type", "=", "product"), ("variant_bom_ids", "=", False), ("status", "=", "confirmed")]`;

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  const searchDomainFilter = `["&",
    ("type", "=", "product"),
    ("variant_bom_ids", "=", False),
    ("status", "=", "confirmed"),
    "|",
      "|",
      ("name", "ilike", "${searchQuery}"),
      ("barcode", "ilike", "${searchQuery}"),
      ("default_code", "ilike", "${searchQuery}")
]`;
  const { items, pages } = useAllProducts({
    page: pageIndex + 1,
    size: pageSize,
    sort_by: 'name',
    sort_order: 'asc',
    domain: searchQuery ? searchDomainFilter : domainFilter,
  });

  const data = useMemo(() => ({ items: items ?? [], pages: pages ?? 1 }), [items, pages]);

  return (
    <SelectProducts
      searchQuery={searchQuery}
      onSearchQueryChange={searchQuery => {
        setSearchQuery(searchQuery);
      }}
      pageSize={pageSize}
      onPageSizeChange={pageSize => {
        setPageSize(pageSize);
      }}
      pageIndex={pageIndex}
      onPaginationClick={direction => {
        const newPageIndex = direction === 'previous' ? pageIndex - 1 : pageIndex + 1;
        setPageIndex(newPageIndex);
      }}
      {...{ saleOrderId, defaultValue, displayOptions, onUpdateSuccess }}
      selectProductsData={data}
      showBadges={showBadges}
    />
  );
};
